import {
  SequenceMigrationDay,
  SequenceMigrationFlag,
  SequenceMigrationLocationRow,
  SequenceMigrationPreviewType,
  SequenceMigrationRowStatus,
  SequenceMigrationScheduleRow,
  SequenceMigrationScopeRow
} from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { BotSequenceMode } from "@swivl-mono/types";
import { Checkbox } from "@swivl-mono/ui";
import { useCallback, useMemo, useState } from "react";

const ROW_STATUS: Record<SequenceMigrationRowStatus, { label: string; className: string }> = {
  create: { label: "New", className: "bg-teal-50 text-teal-700" },
  refresh: { label: "Updates", className: "bg-teal-50 text-teal-700" },
  unchanged: { label: "Up to date", className: "bg-grey-100 text-grey-500" },
  keptEdited: { label: "Edited in Snowy, kept", className: "bg-yellow-50 text-yellow-700" },
  keptSnowy: { label: "Set up in Snowy, kept", className: "bg-yellow-50 text-yellow-700" },
  remove: { label: "Removed, inherits", className: "bg-orange-50 text-orange-700" }
};

/** The PM v3 Sequences page's own names for the modes. */
const MODE_LABEL: Record<BotSequenceMode, string> = {
  [BotSequenceMode.ENABLED]: "On",
  [BotSequenceMode.SIMULATED]: "Simulated",
  [BotSequenceMode.DISABLED]: "Off"
};

/** How a location gets its days, in words. */
const WRITTEN_AS: Record<SequenceMigrationLocationRow["writtenAs"], { label: string; className: string }> = {
  org: { label: "Inherits the account's", className: "text-grey-500" },
  group: { label: "Inherits its group's", className: "text-grey-500" },
  own: { label: "Its own row", className: "text-teal-700" },
  off: { label: "Off here: nothing on in legacy", className: "text-orange-700" },
  kept: { label: "Kept as set up in Snowy", className: "text-yellow-700" }
};

/** Notes shown before "Show all". */
const NOTES_SHOWN = 6;

interface SequenceTypeSectionProps {
  type: SequenceMigrationPreviewType;
  selected: boolean;
  onToggleSelected: (type: SequenceMigrationPreviewType["sequenceType"]) => void;
}

/**
 * One sequence type in the Sequences step: the rows Apply writes, their days (only days On in
 * legacy migrate), sending hours, how each location differs, and the notes to review.
 */
export function SequenceTypeSection({ type, selected, onToggleSelected }: SequenceTypeSectionProps): React.ReactElement {
  const selectable = (type.status === "ready" || type.status === "off") && type.writeCount > 0;
  const handleToggle = useCallback(() => onToggleSelected(type.sequenceType), [onToggleSelected, type.sequenceType]);
  const accountDays = type.rows.find((r) => r.scope === "org")?.days ?? [];

  return (
    <section className={filterClasses("mb-6 rounded border border-grey-200 bg-white", !selected && "opacity-60")}>
      <header className="flex items-center gap-3 border-b border-grey-100 px-4 py-3">
        <Checkbox checked={selected} onCheckedChange={handleToggle} disabled={!selectable} name={`select-${type.sequenceType}`} />
        <h2 className="body-sm-alt text-grey-700">{type.label}</h2>
        <TypeSummary type={type} />
      </header>

      {type.status === "ready" && (
        <div className="divide-y divide-grey-100">
          {type.rows.map((row) => (
            <ScopeRow key={`${row.scope}:${row.scopeId}`} row={row} />
          ))}
          {type.schedules.length > 0 && <Schedules schedules={type.schedules} />}
          {type.locations.length > 0 && <LocationBreakdown locations={type.locations} accountDays={accountDays} />}
        </div>
      )}

      {type.flags.length > 0 && <Notes flags={type.flags} />}
    </section>
  );
}

interface TypeSummaryProps {
  type: SequenceMigrationPreviewType;
}

function TypeSummary({ type }: TypeSummaryProps): React.ReactElement {
  if (type.status === "live") return <span className="body-xs rounded bg-yellow-50 px-2 py-0.5 text-yellow-700">On in Snowy, not re-migrated</span>;
  if (type.status === "none") return <span className="body-xs text-grey-400">No legacy sequence</span>;
  if (type.status === "off")
    return (
      <span className="body-xs text-grey-400">
        Nothing is on in legacy, so nothing migrates
        {type.writeCount > 0 && <span className="ml-3 text-orange-700">removes {type.writeCount} earlier row(s)</span>}
      </span>
    );
  const kept = type.rows.filter((r) => r.status === "keptEdited" || r.status === "keptSnowy").length;
  return (
    <span className="body-xs text-grey-500">
      {type.writeCount > 0 ? <span className="text-teal-600">{type.writeCount} to write</span> : "Up to date"}
      {kept > 0 && <span className="ml-3 text-yellow-700">{kept} kept</span>}
      {type.flags.length > 0 && <span className="ml-3 text-grey-500">{type.flags.length} notes</span>}
    </span>
  );
}

interface ScopeRowProps {
  row: SequenceMigrationScopeRow;
}

function ScopeRow({ row }: ScopeRowProps): React.ReactElement {
  const [expanded, setExpanded] = useState(row.scope === "org");
  const handleToggle = useCallback(() => setExpanded((v) => !v), []);
  const status = ROW_STATUS[row.status];
  const hasDays = !row.off && row.days.length > 0;

  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="body-sm-alt text-grey-800">{row.scope === "org" ? "Whole account" : row.scopeLabel}</span>
        {row.scope !== "org" && <span className="body-xs text-grey-400">{row.scope === "group" ? "group" : "location"}</span>}
        <span className={filterClasses("body-xs rounded px-2 py-0.5", status.className)}>{status.label}</span>
        {row.mode && <span className="body-xs text-grey-500">Mode: {MODE_LABEL[row.mode]}</span>}
        {row.off ? (
          <span className="body-xs text-orange-700">Nothing is on here in legacy, so it&apos;s written as Off</span>
        ) : (
          <span className="body-xs text-grey-500">
            {row.days.length} day{row.days.length === 1 ? "" : "s"}
          </span>
        )}
        {row.changes.length > 0 && row.status !== "unchanged" && <span className="body-xs text-grey-400">{row.changes.slice(0, 3).join(" · ")}</span>}
        {hasDays && (
          <button type="button" className="body-xs ml-auto text-orange-600 hover:underline" onClick={handleToggle}>
            {expanded ? "Hide days" : "Show days"}
          </button>
        )}
      </div>
      {expanded && hasDays && <DaysTable days={row.days} />}
    </div>
  );
}

interface DaysTableProps {
  days: SequenceMigrationDay[];
}

function DaysTable({ days }: DaysTableProps): React.ReactElement {
  return (
    <div className="mt-3 overflow-x-auto rounded border border-grey-100">
      <table className="w-full text-xs">
        <thead className="bg-grey-50 text-left text-grey-500">
          <tr>
            <th className="px-3 py-2 font-medium">Day</th>
            <th className="px-3 py-2 font-medium">Sends</th>
            <th className="px-3 py-2 font-medium">Wording</th>
            <th className="px-3 py-2 font-medium">Text</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-grey-100">
          {days.map((day) => (
            <tr key={day.offsetDays} className="align-top">
              <td className="px-3 py-2 whitespace-nowrap">
                Day {day.offsetDays}
                {!day.enabled && <span className="text-grey-400"> (off in Snowy)</span>}
              </td>
              <td className="px-3 py-2">{channelsOf(day)}</td>
              <td className={filterClasses("px-3 py-2 whitespace-nowrap", day.wording === "legacy" ? "text-teal-700" : "text-grey-500")}>
                {day.wording === "legacy" ? "Custom, from legacy" : "Snowy default"}
              </td>
              <td className="max-w-xl px-3 py-2 break-words whitespace-pre-wrap">{day.text ?? "No text"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function channelsOf(day: SequenceMigrationDay): string {
  const channels = [day.text ? "Text" : null, day.call ? "Call" : null, day.email ? "Email" : null, day.fmsNote ? "FMS note" : null].filter(Boolean);
  const list = channels.length > 0 ? channels.join(", ") : "Nothing";
  return day.sendASAP ? `${list} (immediately)` : list;
}

interface SchedulesProps {
  schedules: SequenceMigrationScheduleRow[];
}

function Schedules({ schedules }: SchedulesProps): React.ReactElement {
  return (
    <div className="px-4 py-3">
      <div className="body-sm-alt mb-2 text-grey-800">Sending hours</div>
      <ul className="flex flex-col gap-1">
        {schedules.map((s) => (
          <li key={`${s.scope}:${s.scopeId}`} className="body-xs flex flex-wrap items-center gap-2 text-grey-600">
            <span>{s.scope === "org" ? "Whole account" : s.scopeLabel}</span>
            <span className="text-grey-800">{s.hours}</span>
            <span className={filterClasses("rounded px-2 py-0.5", ROW_STATUS[s.status].className)}>{ROW_STATUS[s.status].label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface LocationBreakdownProps {
  locations: SequenceMigrationLocationRow[];
  /** The whole account's days, to show a location's own text where it differs. */
  accountDays: SequenceMigrationDay[];
}

/** Every location, Details-style: its days in Snowy, how they reach it, and how they differ. */
function LocationBreakdown({ locations, accountDays }: LocationBreakdownProps): React.ReactElement {
  const differing = locations.filter((l) => l.differences.length > 0 || l.writtenAs === "off" || l.writtenAs === "kept").length;
  const [expanded, setExpanded] = useState(differing > 0);
  const handleToggle = useCallback(() => setExpanded((v) => !v), []);
  const summary =
    differing === 0
      ? `All ${locations.length} locations get the account's days.`
      : `${locations.length - differing} of ${locations.length} locations get the account's days; ${differing} ${differing === 1 ? "differs" : "differ"}.`;

  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="body-sm-alt text-grey-800">By location</span>
        <span className="body-xs text-grey-500">{summary}</span>
        <button type="button" className="body-xs ml-auto text-orange-600 hover:underline" onClick={handleToggle}>
          {expanded ? "Hide locations" : `Show locations (${locations.length})`}
        </button>
      </div>
      {expanded && (
        <div className="mt-3 overflow-x-auto rounded border border-grey-100">
          <table className="w-full text-xs">
            <thead className="bg-grey-50 text-left text-grey-500">
              <tr>
                <th className="px-3 py-2 font-medium">Location</th>
                <th className="px-3 py-2 font-medium">Group</th>
                <th className="px-3 py-2 font-medium">Days in Snowy</th>
                <th className="px-3 py-2 font-medium">How it&apos;s written</th>
                <th className="px-3 py-2 font-medium">Differs from the account</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-grey-100">
              {locations.map((location) => (
                <LocationRow key={location.locationId} location={location} accountDays={accountDays} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface LocationRowProps {
  location: SequenceMigrationLocationRow;
  accountDays: SequenceMigrationDay[];
}

function LocationRow({ location, accountDays }: LocationRowProps): React.ReactElement {
  const writtenAs = WRITTEN_AS[location.writtenAs];
  // A location's own wording, shown under its differences so it can be read without leaving the step.
  const ownTexts = location.days.filter((d) => {
    const account = accountDays.find((a) => a.offsetDays === d.offsetDays);
    return d.text !== null && (!account || account.text !== d.text);
  });
  const matches = location.differences.length === 0 && location.writtenAs !== "off";

  return (
    <tr className={filterClasses("align-top", matches && "text-grey-500")}>
      <td className="px-3 py-2 whitespace-nowrap">{location.locationName}</td>
      <td className="px-3 py-2">{location.groupName ?? "None"}</td>
      <td className="px-3 py-2">{location.days.length > 0 ? location.days.map((d) => `Day ${d.offsetDays}`).join(", ") : "None"}</td>
      <td className={filterClasses("px-3 py-2", writtenAs.className)}>{writtenAs.label}</td>
      <td className="px-3 py-2">
        {matches ? "Same" : location.differences.join("; ")}
        {ownTexts.map((d) => (
          <div key={d.offsetDays} className="mt-1 break-words whitespace-pre-wrap text-grey-500">
            Day {d.offsetDays} text: {d.text}
          </div>
        ))}
      </td>
    </tr>
  );
}

interface NotesProps {
  flags: SequenceMigrationFlag[];
}

/** The notes to review, general ones first, then by day. */
function Notes({ flags }: NotesProps): React.ReactElement {
  const [showAll, setShowAll] = useState(false);
  const handleShowAll = useCallback(() => setShowAll((v) => !v), []);
  const ordered = useMemo(() => [...flags].sort((a, b) => (a.offsetDays ?? -1) - (b.offsetDays ?? -1)), [flags]);
  const shown = showAll ? ordered : ordered.slice(0, NOTES_SHOWN);

  return (
    <div className="border-t border-grey-100 px-4 py-3">
      <div className="body-sm-alt mb-2 text-grey-800">Notes to review</div>
      <ul className="flex flex-col gap-2">
        {shown.map((flag) => (
          <FlagLine key={`${flag.code}:${flag.scopeLabel ?? ""}:${flag.offsetDays ?? ""}:${flag.message}`} flag={flag} />
        ))}
      </ul>
      {ordered.length > NOTES_SHOWN && (
        <button type="button" className="body-xs mt-2 text-orange-600 hover:underline" onClick={handleShowAll}>
          {showAll ? "Show fewer" : `Show all ${ordered.length} notes`}
        </button>
      )}
    </div>
  );
}

interface FlagLineProps {
  flag: SequenceMigrationFlag;
}

function FlagLine({ flag }: FlagLineProps): React.ReactElement {
  return (
    <li className="body-xs flex items-start gap-2 text-grey-700">
      <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-yellow-500" />
      <span>
        {flag.scopeLabel && flag.scopeLabel !== "Org" && <span className="text-grey-500">At {flag.scopeLabel}: </span>}
        {flag.message}
      </span>
    </li>
  );
}
